import { meldeEinstellungsAenderung } from '../platform/einstellungenGeaendert';
import { RELAY_SEED_NS, uuidv5, uuidv7 } from '../platform/uuid';
import type { Store } from './port';
import type { ClientSettingValueRow } from './rows';

/**
 * Die Einstellungen der Oberfläche sind Relay-`ClientSetting`s vom Typ `TABLET_OSCE`.
 *
 * ⚠️ **Die Namen kommen seit dem 2026-09-17 aus `settings/katalog.json`** und heissen dort wie
 * überall sonst (`sheet_half_points` statt `halfPoints`) — Entscheidung des Nutzers, s.
 * `docs/einstellungen-quelle.md`. Wer hier einen Namen von Hand hineinschreibt, legt eine
 * Einstellung an, die der Katalog nicht kennt und deshalb niemand liest; der Weg führt über
 * `platform/einstellungen.ts::relayName`.
 *
 * Die Tabelle `ClientSetting` selbst liegt **nicht** auf dem Gerät — die Kennung ist
 * rechenbar, die Beschriftungen kommen aus dem Sprachkatalog der App.
 *
 * ⚠️ **`CLIENT_TYPE` ist hier bewusst `TABLET_OSCE`, nicht `TABLET`.** `Client.clientType`
 * (siehe `client.ts`) und `ClientSetting.clientType` sind zwei getrennte Relay-Wertelisten —
 * keine Inkonsistenz dieser App, sondern eine Relay-Eigenart (`memory/HIER-WEITERMACHEN.md`).
 */

const CLIENT_TYPE = 'TABLET_OSCE';

export function settingId(name: string): string {
  return uuidv5(RELAY_SEED_NS, 'ClientSetting/' + CLIENT_TYPE + '.' + name);
}

/**
 * Alle Einstellungswerte dieses Geräts, unabhängig davon, ob die App den Namen kennt: ein
 * Schlüssel aus einer neueren Fassung landet einfach als zusätzliches Feld im Ergebnis, statt
 * beim Zurücklesen zu verschwinden.
 */
export async function loadSettings(store: Store, clientId: string): Promise<Record<string, string>> {
  const zeilen = await store.all<ClientSettingValueRow>('ClientSettingValue', { clientID: clientId });
  return Object.fromEntries(zeilen.map((z) => [z.clientSettingName, z.value]));
}

export async function saveSetting(
  store: Store,
  clientId: string,
  name: string,
  value: string,
): Promise<void> {
  const [vorhanden] = (
    await store.all<ClientSettingValueRow>('ClientSettingValue', { clientID: clientId })
  ).filter((z) => z.clientSettingName === name);
  await store.put('ClientSettingValue', [
    {
      id: vorhanden?.id ?? uuidv7(),
      clientID: clientId,
      clientSettingID: settingId(name),
      clientSettingName: name,
      value,
    },
  ]);
  meldeEinstellungsAenderung();
}
